import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, IconButton } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import type { FacultyMember } from '@dosfilos/domain';
import { initials } from '../../../lib/landing-faculty';
import { LANDING } from '../landing-theme';

const GRID_LIMIT = 8;

function Portrait({ member, className }: { member: FacultyMember; className?: string }) {
  return (
    <div className={`fs-ph${className ? ` ${className}` : ''}`}>
      {member.photoUrl ? (
        <img src={member.photoUrl} alt={member.name} loading="lazy" decoding="async" />
      ) : (
        // Sin foto: sus iniciales, en el mismo marco, para que la grilla no tenga huecos.
        <div className="fs-mono" aria-hidden="true">
          {initials(member.name)}
        </div>
      )}
    </div>
  );
}

function Areas({ areas }: { areas: string[] | undefined }) {
  if (!areas?.length) return null;
  return (
    <div className="fs-areas">
      {areas.map((a) => (
        <span key={a}>{a}</span>
      ))}
    </div>
  );
}

/**
 * El cuerpo académico en el landing: las autoridades en una fila destacada con
 * su reseña a la vista, y el resto en una grilla de retratos que abre la ficha.
 * Todo lo escribe el seminario en Configuración → Landing; nada es de relleno.
 */
export function FacultySection({
  members,
  intro,
  gold,
  goldAlpha,
}: {
  /** Ya filtrados y ordenados (`visibleFaculty`). */
  members: FacultyMember[];
  intro?: string | undefined;
  gold: string;
  goldAlpha: (a: number) => string;
}) {
  const { t } = useTranslation('sis');
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState<FacultyMember | null>(null);
  const { SERIF, SANS, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_MUTED, BORDER, RULE, RAISED, BG2 } =
    LANDING;

  const lead = members.filter((m) => m.leadership);
  const rest = members.filter((m) => !m.leadership);
  const shown = showAll ? rest : rest.slice(0, GRID_LIMIT);

  return (
    <section
      id="docentes"
      className="fs"
      style={{ background: BG2, borderTop: `1px solid ${BORDER}` }}
    >
      <style>{`
        .fs-wrap { max-width: 1200px; margin: 0 auto; padding: 96px 64px 88px; font-family: ${SANS}; }
        .fs-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; flex-wrap: wrap; padding-bottom: 28px; border-bottom: 1px solid ${BORDER}; }
        .fs-title { font-family: ${SERIF}; font-weight: 400; font-size: clamp(2rem, 4vw, 3rem); line-height: 1.12; margin: 0; color: ${TEXT_PRIMARY}; }
        .fs-intro { color: ${TEXT_SECONDARY}; font-size: 15.5px; line-height: 1.7; max-width: 58ch; margin: 14px 0 0; }
        .fs-count { font-size: 13px; color: ${TEXT_MUTED}; letter-spacing: .04em; font-variant-numeric: tabular-nums; }
        .fs-role { display: inline-block; font-size: 10.5px; font-weight: 600; letter-spacing: .18em; text-transform: uppercase; color: ${gold}; }
        .fs-deg { font-size: 13px; color: ${TEXT_SECONDARY}; line-height: 1.45; }
        .fs-areas { margin-top: 10px; display: flex; flex-wrap: wrap; gap: 6px; }
        .fs-areas span { font-size: 11.5px; color: ${TEXT_SECONDARY}; border: 1px solid ${RULE}; border-radius: 999px; padding: 3px 9px; }
        .fs-ph { position: relative; overflow: hidden; border-radius: 2px; background: #1a1d1d; border: 1px solid rgba(232,228,220,.06); aspect-ratio: 4 / 5; }
        .fs-ph img { width: 100%; height: 100%; object-fit: cover; display: block; filter: grayscale(.35) brightness(.92); transition: transform .5s ease, filter .5s ease; }
        .fs-ph::after { content: ""; position: absolute; inset: 0; background: linear-gradient(180deg, transparent 55%, rgba(13,15,15,.5)); pointer-events: none; }
        .fs-mono { position: absolute; inset: 0; display: grid; place-items: center; font-family: ${SERIF}; font-size: clamp(2rem, 4vw, 3.2rem); color: ${goldAlpha(0.6)}; background: radial-gradient(80% 70% at 50% 30%, ${goldAlpha(0.1)}, transparent 70%), #161919; }
        .fs-lead { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; margin-top: 40px; }
        .fs-lead.is-single { grid-template-columns: minmax(0, 760px); }
        .fs-lead-card { display: grid; grid-template-columns: 168px minmax(0, 1fr); gap: 24px; align-items: start; padding: 22px; border: 1px solid ${RULE}; border-radius: 3px; background: linear-gradient(180deg, #171a1a, ${RAISED}); }
        .fs-lead-card h3 { font-family: ${SERIF}; font-weight: 500; font-size: 26px; line-height: 1.1; margin: 8px 0 6px; color: ${TEXT_PRIMARY}; }
        .fs-bio { margin: 14px 0 0; font-size: 14px; line-height: 1.65; color: ${TEXT_SECONDARY}; display: -webkit-box; -webkit-line-clamp: 5; -webkit-box-orient: vertical; overflow: hidden; }
        .fs-more { margin-top: 12px; background: none; border: 0; padding: 0; font-family: ${SANS}; font-size: 12px; letter-spacing: .1em; text-transform: uppercase; color: ${gold}; cursor: pointer; }
        .fs-more:hover { text-decoration: underline; text-underline-offset: 4px; }
        .fs-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 28px 20px; margin-top: 40px; }
        .fs-card { text-align: left; background: none; border: 0; padding: 0; cursor: pointer; display: flex; flex-direction: column; gap: 4px; font-family: ${SANS}; }
        .fs-card .fs-ph { margin-bottom: 12px; }
        .fs-card h3 { font-family: ${SERIF}; font-weight: 500; font-size: 20px; line-height: 1.15; margin: 4px 0 0; color: ${TEXT_PRIMARY}; transition: color .2s; }
        .fs-card:hover h3 { color: ${gold}; }
        .fs-card:hover .fs-ph img { transform: scale(1.03); filter: none; }
        .fs-card:focus-visible, .fs-more:focus-visible, .fs-toggle:focus-visible { outline: 2px solid ${goldAlpha(0.8)}; outline-offset: 4px; }
        .fs-all { margin-top: 36px; display: flex; justify-content: center; }
        @media (max-width: 1023px) {
          .fs-wrap { padding: 64px 24px 56px; }
          .fs-lead { grid-template-columns: minmax(0, 1fr); }
          .fs-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 639px) {
          .fs-lead-card { grid-template-columns: 110px minmax(0, 1fr); gap: 16px; padding: 16px; }
          .fs-lead-card h3 { font-size: 21px; }
          .fs-bio { -webkit-line-clamp: 4; font-size: 13.5px; }
          .fs-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 22px 14px; }
          .fs-card h3 { font-size: 17.5px; }
        }
        @media (prefers-reduced-motion: reduce) { .fs-ph img, .fs-card h3 { transition: none; } }
      `}</style>
      <div className="fs-wrap">
        <div className="fs-head">
          <div>
            <p className="section-label">{t('publicLanding.faculty.label')}</p>
            <h2 className="fs-title">{t('publicLanding.faculty.title')}</h2>
            {intro && <p className="fs-intro">{intro}</p>}
          </div>
          <span className="fs-count">
            {t('publicLanding.faculty.count', { count: members.length })}
          </span>
        </div>

        {lead.length > 0 && (
          // Con una sola autoridad, una columna: media fila vacía se ve como un error.
          <div className={`fs-lead${lead.length === 1 ? ' is-single' : ''}`}>
            {lead.map((m) => (
              <article key={m.id} className="fs-lead-card">
                <Portrait member={m} />
                <div>
                  <span className="fs-role">{m.role}</span>
                  <h3>{m.name}</h3>
                  {m.degree && <div className="fs-deg">{m.degree}</div>}
                  <Areas areas={m.areas} />
                  {m.bio && (
                    <>
                      <p className="fs-bio">{m.bio}</p>
                      <button type="button" className="fs-more" onClick={() => setOpen(m)}>
                        {t('publicLanding.faculty.readMore')}
                      </button>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {shown.length > 0 && (
          <div className="fs-grid">
            {shown.map((m) => (
              <button
                key={m.id}
                type="button"
                className="fs-card"
                onClick={() => setOpen(m)}
                aria-label={t('publicLanding.faculty.openProfile', { name: m.name })}
              >
                <Portrait member={m} />
                <span className="fs-role">{m.role}</span>
                <h3>{m.name}</h3>
                {m.degree && <span className="fs-deg">{m.degree}</span>}
              </button>
            ))}
          </div>
        )}

        {rest.length > GRID_LIMIT && (
          <div className="fs-all">
            <button
              type="button"
              className="btn-ghost fs-toggle"
              style={{ fontFamily: SANS }}
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll
                ? t('publicLanding.faculty.showLess')
                : t('publicLanding.faculty.showAll', { count: rest.length })}
            </button>
          </div>
        )}
      </div>

      <Dialog
        open={open !== null}
        onClose={() => setOpen(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: BG2,
            color: TEXT_PRIMARY,
            backgroundImage: 'none',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '4px',
            overflow: 'hidden',
          },
        }}
      >
        {open && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              fontFamily: SANS,
            }}
          >
            <div style={{ minHeight: 280 }}>
              <Portrait member={open} className="fs-dlg-ph" />
            </div>
            <div style={{ padding: '28px 30px 30px', position: 'relative' }}>
              <IconButton
                aria-label={t('publicLanding.faculty.close')}
                onClick={() => setOpen(null)}
                sx={{ position: 'absolute', top: 10, right: 10, color: TEXT_SECONDARY }}
              >
                <CloseRounded />
              </IconButton>
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: gold,
                }}
              >
                {open.role}
              </span>
              <h3
                style={{
                  fontFamily: SERIF,
                  fontWeight: 500,
                  fontSize: 30,
                  lineHeight: 1.08,
                  margin: '8px 40px 8px 0',
                }}
              >
                {open.name}
              </h3>
              {open.degree && (
                <div style={{ fontSize: 13.5, color: TEXT_SECONDARY }}>{open.degree}</div>
              )}
              {open.areas?.length ? (
                <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {open.areas.map((a) => (
                    <span
                      key={a}
                      style={{
                        fontSize: 11.5,
                        color: TEXT_SECONDARY,
                        border: `1px solid ${RULE}`,
                        borderRadius: 999,
                        padding: '3px 9px',
                      }}
                    >
                      {a}
                    </span>
                  ))}
                </div>
              ) : null}
              {open.bio && (
                <p style={{ margin: '18px 0 0', fontSize: 15, lineHeight: 1.7, color: '#d6d1c8' }}>
                  {open.bio}
                </p>
              )}
            </div>
          </div>
        )}
      </Dialog>
    </section>
  );
}
